import React, { useContext, useEffect } from 'react';

import styles from './styles.module.scss';
import Accordion, { AccordionItem } from '../../Accordion/index.js';
import { AlertContext, CLEAR_ALERT, CRITICAL_ALERT } from '../../Alert/index.js';
import Banner from '../../Banner/index.js';
import Spacer from '../../Spacer/index.ts';

function stringifyError(error: Error | undefined) {
  return JSON.stringify(
    error,
    (_, value: Record<string, unknown>) => {
      if (value instanceof Error) {
        const obj: Record<string, unknown> = {};

        Object.getOwnPropertyNames(value).forEach((key: string) => {
          obj[key] = value[key];
        });

        return obj;
      }

      return value;
    },
    2
  );
}

export interface ErrorBoundaryProps {
  error: Error;
  componentStack: string | null;
  eventId: string | null;
  resetError(): void;
}

function ErrorBanner({ eventId, resetError, error }: Omit<ErrorBoundaryProps, 'componentStack'>) {
  const { dispatchAlert } = useContext(AlertContext);

  const dismissAlert = () => {
    resetError();
    dispatchAlert({
      type: CLEAR_ALERT,
      id: error?.message
    });
  };
  useEffect(() => {
    dispatchAlert({
      type: CRITICAL_ALERT,
      id: error?.message || 'UNKNOWN_ERROR',
      message: error?.message || 'An unexpected error has occurred',
      onDismiss: dismissAlert
    });
  });

  return (
    <Banner type="critical" onClose={dismissAlert} isDismissable spacing={{ x: 5, y: 2 }}>
      <h1 className={styles['aglmd-error-fallback__title']}>
        An unexpected error occurred: {eventId}!
      </h1>
      <div>Dismissing this banner or the alert above will attempt retry to display the page</div>
    </Banner>
  );
}

/**
 * A fallback to show when an error occurred in a page
 */
const ErrorFallback: React.FC<ErrorBoundaryProps> = ({
  error,
  resetError,
  componentStack,
  eventId
}: ErrorBoundaryProps) => (
  <>
    <ErrorBanner error={error} resetError={resetError} eventId={eventId} />
    {process.env.NODE_ENV !== 'production' && (
      <Spacer x={4}>
        <Accordion initialOpenAccordions="error-id">
          <AccordionItem header="Error ID" value="error-id">
            <pre>{eventId}</pre>
          </AccordionItem>
          <AccordionItem header="Error" value="error">
            <pre>{stringifyError(error)}</pre>
          </AccordionItem>
          <AccordionItem header="Stack Trace" value="error-stack">
            <pre>{componentStack}</pre>
          </AccordionItem>
        </Accordion>
      </Spacer>
    )}
  </>
);

export default ErrorFallback;
